import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, FolderKanban, Loader2, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, getStatusTone } from '@/lib/legalOs';
import { loadTodaySummary } from '@/lib/workflowData';
import { AppEmptyState, AppMetricCard, AppPage, AppPageHeader, AppSectionHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

function SurfaceCard({ title, children, action }) {
  return (
    <section className="advoq-card" style={{ padding: '18px', borderRadius: '24px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'grid', gap: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px' }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function DraftingHubPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadTodaySummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Drafting — Advoq';
    loadSummary();
  }, [loadSummary]);

  const filingBoards = useMemo(
    () => (summary?.matter_boards || []).filter((item) => item?.filing_readiness),
    [summary?.matter_boards],
  );

  return (
    <main className="app-main" data-testid="drafting-hub-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Drafting"
          title="Drafting and filing factory"
          description="Turn live matter posture into court-ready drafts, filing checklists, annexure discipline, and cleaner final output."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
          meta={(
            <div className="app-chip-row">
              <span className="app-page-chip">Workflow-first drafting</span>
              <span className="app-page-chip">Filing readiness is explicit</span>
            </div>
          )}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        <div className="app-responsive-metrics-grid">
          <AppMetricCard icon={FileText} label="Matter drafting lanes" value={filingBoards.length} note="Matters with filing-readiness data already attached." />
          <AppMetricCard icon={CheckCircle2} label="Readiness checks" value={filingBoards.reduce((sum, item) => sum + ((item.filing_readiness?.checklist || []).length), 0)} note="Checklist items visible across the current drafting queue." accent="#0f766e" />
          <AppMetricCard icon={FolderKanban} label="Output bundles" value={filingBoards.reduce((sum, item) => sum + ((item.filing_readiness?.output_bundle || []).length), 0)} note="Draft, chronology, annexure, and client-update outputs already mapped." accent="#2563eb" />
        </div>

        <div className="app-split-grid">
          <SurfaceCard title="Drafting entry points" action={<button type="button" className="app-chat-button" onClick={() => navigate('/documents')}>Open documents <ArrowRight size={14} /></button>}>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { title: 'Working drafts', detail: 'Open the live drafting desk for petitions, notices, responses, and chamber working papers.', path: '/documents' },
                { title: 'Precedents', detail: 'Use structured templates and reusable patterns before opening a blank drafting surface.', path: '/templates' },
                { title: 'Matter boards', detail: 'Jump into matter-linked filing readiness before hard drafting or export.', path: '/matters/dashboard' },
              ].map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="advoq-card"
                  style={{ textAlign: 'left', padding: '16px', borderRadius: '20px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)', display: 'grid', gap: '8px' }}
                >
                  <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{item.title}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>{item.detail}</div>
                </button>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard title="Workflow guardrails">
            <div style={{ display: 'grid', gap: '10px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
              <div>Lead with the actual filing lane, not a generic blank draft.</div>
              <div>Keep the trigger document, chronology, authority pack, and annexure map explicit before final circulation.</div>
              <div>Use client-safe updates and team tasks as part of the drafting workflow, not as separate afterthoughts.</div>
            </div>
          </SurfaceCard>
        </div>

        <section style={{ display: 'grid', gap: '16px' }}>
          <AppSectionHeader
            eyebrow="Matter-linked drafting"
            title="Filing readiness queue"
            description="The matters where drafting should move together with readiness checks, defect radar, and output bundles."
          />
          {!loading && !filingBoards.length ? (
            <AppEmptyState
              icon={FileText}
              title="No filing-readiness lanes yet"
              description="As matter ops surfaces target filings and defects, the drafting queue will become visible here."
            />
          ) : null}
          {filingBoards.length ? (
            <div style={{ display: 'grid', gap: '16px' }}>
              {filingBoards.map((board) => (
                <SurfaceCard
                  key={board.matter_id}
                  title={board.title}
                  action={<button type="button" className="app-chat-button" onClick={() => navigate(`/war-room/${board.matter_id}`)}>Open matter <ArrowRight size={14} /></button>}
                >
                  <div className="app-chip-row">
                    <span className="app-page-chip">{board.filing_readiness?.bundle_label || 'Scoping'}</span>
                    {board.filing_readiness?.target_filing ? <span className="app-page-chip">{board.filing_readiness.target_filing}</span> : null}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
                    {compactText(board.overview, 220)}
                  </div>
                  <div className="app-detail-grid">
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {(board.filing_readiness?.checklist || []).slice(0, 4).map((item, index) => {
                        const tone = getStatusTone(item.status);
                        return (
                          <div key={`${board.matter_id}_check_${index}`} className="advoq-card" style={{ padding: '12px', borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.label}</div>
                              <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>{item.status}</span>
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6, marginTop: '6px' }}>{compactText(item.detail, 120)}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {(board.filing_readiness?.output_bundle || []).slice(0, 4).map((item, index) => (
                        <div key={`${board.matter_id}_bundle_${index}`} className="advoq-card" style={{ padding: '12px', borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>{item.title}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6, marginTop: '6px' }}>{compactText(item.detail, 120)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </SurfaceCard>
              ))}
            </div>
          ) : null}
        </section>
      </AppPage>
    </main>
  );
}
